import { FirebaseContext } from "@helpers/firebase";
import { useBranding } from "@helpers/useBranding";
import { useClaims } from "@helpers/useClaims";
import { usePersistedState } from "@helpers/usePersistedState";
import { EditChallenges } from "@pages/editchallenges";
import { Report } from "@pages/report";
import { FC, ReactNode, Suspense, lazy, useContext, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { decodePin } from "src/helpers/codec";
import { DeeplinkJoin } from "./pages/challenge/DeeplinkJoin";
import { UpgradeNow } from "./components/UpgradeNow/UpgradeNow";
import { useEventId } from "./helpers/useEvent";
import { useEnsureEventLocale } from "./helpers/useEnsureEventLocale";
import { useToast } from "./helpers/useToast";
import { AddEvent } from "./pages/addevent";
import { Admin } from "./pages/admin";
import { Challenge } from "./pages/challenge";
import { ChallengeQuestion } from "./pages/challenge/quiz/question";
import { Challenges } from "./pages/challenges";
import { Contact } from "./pages/contact";
import { Customise } from "./pages/customise";
import { ForgotPassword } from "./pages/forgotpassword";
import { Gallery } from "./pages/gallery";
import { Agenda } from "./pages/agenda";
import { AgendaFirst } from "./pages/agenda-first/AgendaFirst";
import { GuestGallery } from "./pages/guestgallery";
import { Highscores } from "./pages/highscores";
import { Intro } from "./pages/intro";
import { Landing } from "./pages/landing";
import { Login } from "./pages/login";
import { ManageTeams } from "./pages/manageteams";
import { MyBranding } from "./pages/mybranding";
import { MyEvents } from "./pages/myevents";
import { NewTeam } from "./pages/newteam";
import { NewUser } from "./pages/newuser";
import { OracleDashboard } from "./pages/oracle";
import { OracleTeamPicker } from "./pages/oracle/TeamPicker";
import { OracleTeam } from "./pages/oracle/OracleTeam";
import { PaymentsCancel } from "./pages/payments/Cancel";
import { PaymentsSuccess } from "./pages/payments/Success";
import { PublicGallery } from "./pages/publicgallery";
import { PublicBettingResults } from "./pages/publicbetting";
import { Register } from "./pages/register";
import { RegisterPrepopulate } from "./pages/registerprepopulate";
import { Select } from "./pages/select";
import { SelectTeam } from "./pages/selectteam";
import { Slideshow } from "./pages/slideshow";
import { Teams } from "./pages/teams";
import { TeamSettings } from "./pages/teamsettings";
import { Upload } from "./pages/upload";
import { Uploader } from "./pages/uploader";
import { WheelOfFortune } from "./pages/wheel";

// ── Camdeed Studio (isolated feature — docs/studio/): lazy-loaded so the Studio
// chunk never bloats the core bundle. Deleting Studio = remove this + its route.
const Studio = lazy(() => import("./pages/studio").then((m) => ({ default: m.StudioRoutes })));

// Legal pages (long-form English text): lazy-loaded, public, brand-aware.
const Terms = lazy(() => import("./pages/legal/Terms").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("./pages/legal/Privacy").then((m) => ({ default: m.Privacy })));
const StudioTerms = lazy(() => import("./pages/legal/StudioTerms").then((m) => ({ default: m.StudioTerms })));

const LoginRedirect = () => {
  return <Navigate to={`/login`} />;
};

const LandingRedirect = () => {
  const { homepage } = useBranding();
  window.location.href = homepage;
  return null;
};

const Fallback = () => {
  console.log("Fallback");
  return null;
};

type GuardProps = {
  children: ReactNode;
  claims: Claims;
};

const LandingGuard: FC<GuardProps> = ({ children, claims }) => {
  const eventId = useEventId();

  const go = (path: string) => {
    if (eventId) return <Navigate to={`/${eventId}${path}`} />;
  };

  const validEventIds = claims ? claims.events || [claims.eventId] : [];
  const correctEventId = validEventIds.includes(eventId);

  if (claims?.role === "user" && correctEventId) return go("/challenges");
  if (claims?.role === "uploader" && correctEventId) return go("/uploader");
  if (claims?.role === "admin" && correctEventId) return go("/admin");
  if (claims?.role === "oracle" && correctEventId) return go("/oracle");

  return <>{children}</>;
};

// Covers special case where Uploader user clicks on "Visit Event Game" and lands on /teams page
// Where they will be creating a new team or joining an existing team (effectively changing their role on the JWT claims)
const TeamsPageGuard: FC<GuardProps> = ({ children, claims }) => {
  const eventId = useEventId();
  const go = (path?: string) => {
    if (!path && eventId) return <Navigate to={`/${eventId}`} />;
    if (!path) return <Navigate to="/" />;
    if (eventId) return <Navigate to={`/${eventId}${path}`} />;
    return <Navigate to={path} />;
  };

  // Only redirect by role if claims belong to this event.
  // Stale claims from a different event should not block onboarding.
  const validEventIds = claims ? claims.events || [claims.eventId] : [];
  const correctEventId = validEventIds.includes(eventId);

  if (claims?.role === "user" && correctEventId) return go("/challenges");
  if (claims?.role === "admin" && correctEventId) return go("/admin");
  if (claims?.role === "oracle" && correctEventId) return go("/oracle");
  if (claims?.role === "public" && correctEventId) return go();

  return <>{children}</>;
};

const NonAdminGuard: FC<GuardProps> = ({ children, claims }) => {
  const eventId = useEventId();
  const go = (path?: string) => {
    if (!path && eventId) return <Navigate to={`/${eventId}`} />;
    if (!path) return <Navigate to="/" />;
    if (eventId) return <Navigate to={`/${eventId}${path}`} />;
    return <Navigate to={path} />;
  };

  // Only redirect by role if claims belong to this event.
  // Stale claims from a different event should not block onboarding.
  const validEventIds = claims ? claims.events || [claims.eventId] : [];
  const correctEventId = validEventIds.includes(eventId);

  if (claims?.role === "user" && correctEventId) return go("/challenges");
  if (claims?.role === "uploader" && correctEventId) return go("/uploader");
  if (claims?.role === "admin" && correctEventId) return go("/admin");
  if (claims?.role === "oracle" && correctEventId) return go("/oracle");
  if (claims?.role === "public" && correctEventId) return go();

  return <>{children}</>;
};

const UploaderGuard: FC<GuardProps> = ({ children, claims }) => {
  const [appData] = usePersistedState();
  const eventId = useEventId();

  const go = (path?: string) => {
    if (!path && eventId) return <Navigate to={`/${eventId}`} />;
    if (!path) return <Navigate to="/" />;
    if (eventId) return <Navigate to={`/${eventId}${path}`} />;
    return <Navigate to={path} />;
  };

  const lostSession = !claims;
  if (lostSession) return go();

  const validEventIds = claims.events || [claims.eventId];
  const isWrongEvent = !validEventIds.includes(eventId);
  if (isWrongEvent) return go();

  // Oracles have no teamId, so the team check below would pass when appData is empty — redirect first.
  if (claims.role === "oracle") return go("/oracle");

  const isWrongTeam = appData?.uid !== claims.teamId;
  if (isWrongTeam) return go();

  const isAdmin = claims.role === "admin";
  if (isAdmin) return go("/admin");

  const isUser = claims.role === "user";
  if (isUser) return go("/challenges");

  const isPublic = claims.role === "public";
  if (isPublic) return go();

  return <>{children}</>;
};

const UserGuard: FC<GuardProps> = ({ children, claims }) => {
  const [appData] = usePersistedState();
  const eventId = useEventId();

  const go = (path?: string) => {
    if (!path && eventId) return <Navigate to={`/${eventId}`} />;
    if (path && eventId) return <Navigate to={`/${eventId}${path}`} />;
    if (path) return <Navigate to={path} />;
  };
  const lostSession = !claims;
  if (lostSession) return go();

  const validEventIds = claims.events || [claims.eventId];
  const isWrongEvent = !validEventIds.includes(eventId);
  if (isWrongEvent) return go();

  // Oracles have no teamId, so the team check below would pass when appData is empty — redirect first.
  if (claims.role === "oracle") return go("/oracle");

  const isWrongTeam = appData?.uid !== claims.teamId;
  if (isWrongTeam) return go();

  const isAdmin = claims.role === "admin";
  if (isAdmin) return go("/admin");

  const isUploader = claims.role === "uploader";
  if (isUploader) return go("/uploader");

  return <>{children}</>;
};

// Challenge pages support QR deeplinks: `?p=<encoded event pin>` plus an optional `&teamId=`.
// With a working session this is exactly UserGuard. Without one, instead of bouncing the guest to
// the landing page (losing the challenge URL), we authenticate on the spot: straight into the
// linked team when teamId is present (passwordless teams only, enforced server-side), otherwise
// through the normal funnel with the destination remembered (appData.deeplink).
const ChallengeGuard: FC<GuardProps> = ({ children, claims }) => {
  const [appData] = usePersistedState();
  const eventId = useEventId();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const pinEncoded = params.get("p");
  const teamIdParam = params.get("teamId");

  const validEventIds = claims ? claims.events || [claims.eventId] : [];
  const correctEventId = validEventIds.includes(eventId);
  const correctTeam = !!claims && appData?.uid === claims.teamId;
  // Team members need the right team; admin/uploader/oracle keep their UserGuard redirects.
  const hasUsableSession =
    !!claims &&
    correctEventId &&
    (claims.role === "user" ? correctTeam : ["admin", "uploader", "oracle"].includes(claims.role));

  if (!pinEncoded || hasUsableSession) return <UserGuard claims={claims}>{children}</UserGuard>;

  const pin = decodePin(pinEncoded);
  if (!pin) return <UserGuard claims={claims}>{children}</UserGuard>;

  return <DeeplinkJoin pin={pin} teamId={teamIdParam} />;
};

/** Same session/event/team checks as UserGuard, but allows uploaders through (e.g. agenda). */
// Only used for agenda page - which is only for onninen event - remove later
const UserOrUploaderGuard: FC<GuardProps> = ({ children, claims }) => {
  const [appData] = usePersistedState();
  const eventId = useEventId();

  const go = (path?: string) => {
    if (!path && eventId) return <Navigate to={`/${eventId}`} />;
    if (path && eventId) return <Navigate to={`/${eventId}${path}`} />;
    if (path) return <Navigate to={path} />;
  };

  const lostSession = !claims;
  if (lostSession) return go();

  const validEventIds = claims.events || [claims.eventId];
  const isWrongEvent = !validEventIds.includes(eventId);
  if (isWrongEvent) return go();

  if (claims.role === "oracle") return go("/oracle");

  const isWrongTeam = appData?.uid !== claims.teamId;
  if (isWrongTeam) return go();

  const isAdmin = claims.role === "admin";
  if (isAdmin) return go("/admin");

  const isParticipant = claims.role === "user" || claims.role === "uploader";
  if (!isParticipant) return go();

  return <>{children}</>;
};

const LightAdminGuard: FC<GuardProps> = ({ children, claims }) => {
  const go = (path) => {
    return <Navigate to={path} />;
  };

  const lostSession = !claims;
  if (lostSession) return go("/login");

  const isAdmin = claims.role === "admin";
  if (!isAdmin) return go("/login");

  return <>{children}</>;
};

const LoginGuard: FC<GuardProps> = ({ children, claims }) => {
  const eventIdFallback = useEventId();
  const eventId = claims?.eventId ?? eventIdFallback;
  const location = useLocation();

  const go = (path) => {
    return <Navigate to={path} />;
  };

  // ── Camdeed Studio (docs/studio/): an already-signed-in user arriving from the
  // /studio waitlist (?returnTo=/studio…) goes straight back instead of to event admin.
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  if (returnTo?.startsWith("/studio") && claims) return go(returnTo);

  const isAdmin = claims?.role === "admin";
  if (isAdmin) {
    // Backstop for already-signed-in visitors — fresh sign-ins navigate inline from the
    // login page itself. Land on the event the claims point at, but only when it is
    // actually among the account's events: a stale eventId would bounce off AdminGuard
    // back here and loop. Anything ambiguous goes to the /events picker.
    const validEventIds = (claims.events || [claims.eventId]).filter(Boolean);
    if (claims.eventId && validEventIds.includes(claims.eventId)) return go(`/${claims.eventId}/admin`);
    if (validEventIds.length === 1) return go(`/${validEventIds[0]}/admin`);
    return go("/events");
  }

  const isOracle = claims?.role === "oracle";
  if (isOracle) return go(`/${eventId}/oracle`);

  return <>{children}</>;
};

// Semi-admin associate ("oracle") guard: same session/event checks as the participant guards, but
// allows only the oracle role through. Other roles are redirected to their own home.
const OracleGuard: FC<GuardProps> = ({ children, claims }) => {
  const eventId = useEventId();
  useEnsureEventLocale();

  const go = (path?: string) => {
    if (!path && eventId) return <Navigate to={`/${eventId}`} />;
    if (!path) return <Navigate to="/" />;
    if (eventId) return <Navigate to={`/${eventId}${path}`} />;
    return <Navigate to={path} />;
  };

  const lostSession = !claims;
  if (lostSession) return go();

  const validEventIds = claims.events || [claims.eventId];
  const isWrongEvent = !validEventIds.includes(eventId);
  if (isWrongEvent) return go();

  if (claims.role === "admin") return go("/admin");
  if (claims.role !== "oracle") return go();

  return <>{children}</>;
};

const AdminGuard: FC<GuardProps> = ({ children, claims }) => {
  const eventId = useEventId();
  useEnsureEventLocale();

  const go = (path) => {
    return <Navigate to={path} />;
  };

  // Render a static fallback instead of navigating to /login. This gives
  // Firebase time to asynchronously restore the session from IndexedDB —
  // once claims resolve, AppRoutes re-renders and this is replaced by admin
  // content. A <Navigate> would fire before the async restore completes and
  // could clear the in-flight session.
  if (!claims)
    return (
      <div className="mt-12 flex flex-col items-center justify-center">
        <div>Your session has timed out</div>
        <div>
          <a className="link mt-6" href="/login">
            Back to Login
          </a>
        </div>
      </div>
    );

  const validEventIds = claims.events || [claims.eventId];
  const wrongEvent = !validEventIds.includes(eventId);
  if (wrongEvent) return go("/login");

  const isAdmin = claims.role === "admin";
  if (!isAdmin) return go("/login");

  return (
    <>
      <UpgradeNow />
      {children}
    </>
  );
};

const SuperuserGuard: FC<GuardProps> = ({ children, claims }) => {
  if (!claims) return null;
  const isAdmin = claims.role === "admin";
  if (!isAdmin) return <Navigate to="/login" />;
  return <>{children}</>;
};

export const AppRoutes: FC = () => {
  const { t } = useTranslation("home");
  const { auth } = useContext(FirebaseContext);
  const [user, userLoading, userError] = useAuthState(auth);
  const [claims, claimsLoading, claimsError] = useClaims(user);
  const toast = useToast();
  const eventId = useEventId();
  const location = useLocation();

  useEffect(() => {
    if (userError) {
      toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      console.error(`Error occured in AppRoutes. EventId: ${eventId}, Error: ${userError}`);
    }
  }, [userError]);

  useEffect(() => {
    if (claimsError) {
      toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      console.error(`Error occured in AppRoutes. EventId: ${eventId}, Error: ${claimsError}`);
    }
  }, [claimsError]);

  // Registration signs the freshly created admin in mid-page; blanking the tree while
  // their claims load would unmount the register page and lose the premium modal step.
  // These routes are unguarded, so they can render without waiting for claims.
  const claimsFreePaths = ["/register", "/registernow"];
  const isClaimsFreePath = claimsFreePaths.includes(location.pathname.replace(/\/+$/, ""));
  if ((userLoading || claimsLoading) && !isClaimsFreePath) return null;

  return (
    <Routes>
      <Route
        path="/report"
        element={
          <SuperuserGuard claims={claims}>
            <Report />
          </SuperuserGuard>
        }
      />
      <Route
        path="/events"
        element={
          <LightAdminGuard claims={claims}>
            <MyEvents />
          </LightAdminGuard>
        }
      />
      <Route
        path="/branding"
        element={
          <LightAdminGuard claims={claims}>
            <MyBranding />
          </LightAdminGuard>
        }
      />
      <Route
        path="/addevent"
        element={
          <LightAdminGuard claims={claims}>
            <AddEvent />
          </LightAdminGuard>
        }
      />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/admin"
        element={
          <LoginGuard claims={claims}>
            <Login />
          </LoginGuard>
        }
      />
      <Route
        path="/login"
        element={
          <LoginGuard claims={claims}>
            <Login />
          </LoginGuard>
        }
      />
      <Route path="/forgotpassword" element={<ForgotPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/registernow" element={<RegisterPrepopulate />} />
      <Route path="/gallery/:shareUuid" element={<PublicGallery />} />
      <Route path="/:eventId/bets" element={<PublicBettingResults />} />

      {/* Public legal pages. Static segments outrank /:eventId; the slugs are also
          in the registration reserved-word list so no event can claim them. */}
      <Route
        path="/terms"
        element={
          <Suspense fallback={null}>
            <Terms />
          </Suspense>
        }
      />
      <Route
        path="/privacy"
        element={
          <Suspense fallback={null}>
            <Privacy />
          </Suspense>
        }
      />
      <Route
        path="/studio-terms"
        element={
          <Suspense fallback={null}>
            <StudioTerms />
          </Suspense>
        }
      />

      {/* Camdeed Studio (isolated feature — docs/studio/). Static segment outranks /:eventId. */}
      <Route
        path="/studio/*"
        element={
          <Suspense fallback={null}>
            <Studio />
          </Suspense>
        }
      />

      <Route
        path="/:eventId"
        element={
          <LandingGuard claims={claims}>
            <Landing />
          </LandingGuard>
        }
      />
      <Route
        path="/:eventId/highscores"
        element={
          <UserGuard claims={claims}>
            <Highscores isAdmin={claims?.role === "admin"} />
          </UserGuard>
        }
      />
      <Route
        path="/:eventId/guest-gallery"
        element={
          <UserGuard claims={claims}>
            <GuestGallery isAdmin={claims?.role === "admin"} />
          </UserGuard>
        }
      />
      <Route
        path="/:eventId/agenda"
        element={
          <UserOrUploaderGuard claims={claims}>
            <Agenda isAdmin={claims?.role === "admin"} isUploader={claims?.role === "uploader"} />
          </UserOrUploaderGuard>
        }
      />
      <Route
        path="/:eventId/upload-guest-gallery"
        element={
          <UploaderGuard claims={claims}>
            <GuestGallery isAdmin={claims?.role === "admin"} isUploader={true} />
          </UploaderGuard>
        }
      />
      <Route
        path="/:eventId/intro"
        element={
          <LandingGuard claims={claims}>
            <Intro />
          </LandingGuard>
        }
      />
      <Route
        path="/:eventId/agenda-first"
        element={
          <LandingGuard claims={claims}>
            <AgendaFirst />
          </LandingGuard>
        }
      />
      <Route
        path="/:eventId/newteam"
        element={
          <TeamsPageGuard claims={claims}>
            <NewTeam />
          </TeamsPageGuard>
        }
      />
      <Route
        path="/:eventId/newuser"
        element={
          <NonAdminGuard claims={claims}>
            <NewUser />
          </NonAdminGuard>
        }
      />
      <Route
        path="/:eventId/teams"
        element={
          <TeamsPageGuard claims={claims}>
            <Teams />
          </TeamsPageGuard>
        }
      />

      <Route
        path="/:eventId/admin"
        element={
          <AdminGuard claims={claims}>
            <Admin />
          </AdminGuard>
        }
      />
      <Route
        path="/:eventId/slideshow"
        element={
          <AdminGuard claims={claims}>
            <Slideshow />
          </AdminGuard>
        }
      />
      <Route
        path="/:eventId/wheel"
        element={
          <AdminGuard claims={claims}>
            <WheelOfFortune />
          </AdminGuard>
        }
      />
      <Route
        path="/:eventId/manageteams"
        element={
          <AdminGuard claims={claims}>
            <ManageTeams />
          </AdminGuard>
        }
      />
      <Route
        path="/:eventId/gallery"
        element={
          <AdminGuard claims={claims}>
            <Gallery />
          </AdminGuard>
        }
      />
      <Route
        path="/:eventId/customise"
        element={
          <AdminGuard claims={claims}>
            <Customise />
          </AdminGuard>
        }
      />
      <Route path="/:eventId/payments/success" element={<PaymentsSuccess />} />
      <Route path="/:eventId/payments/cancel" element={<PaymentsCancel />} />
      <Route
        path="/:eventId/selectteam"
        element={
          <TeamsPageGuard claims={claims}>
            <SelectTeam />
          </TeamsPageGuard>
        }
      />
      <Route
        path="/:eventId/select"
        element={
          <NonAdminGuard claims={claims}>
            <Select />
          </NonAdminGuard>
        }
      />
      <Route
        path="/:eventId/challenges/:challengeId"
        element={
          <ChallengeGuard claims={claims}>
            <Challenge />
          </ChallengeGuard>
        }
      />
      <Route
        path="/:eventId/upload"
        element={
          <UserGuard claims={claims}>
            <Upload />
          </UserGuard>
        }
      />
      <Route
        path="/:eventId/uploader"
        element={
          <UploaderGuard claims={claims}>
            <Uploader />
          </UploaderGuard>
        }
      />
      <Route
        path="/:eventId/teamsettings"
        element={
          <UserGuard claims={claims}>
            <TeamSettings />
          </UserGuard>
        }
      />
      <Route
        path="/:eventId/challenges/:challengeId/:pid"
        element={
          <UserGuard claims={claims}>
            <ChallengeQuestion />
          </UserGuard>
        }
      />
      <Route
        path="/:eventId/challenges"
        element={
          <UserGuard claims={claims}>
            <Challenges />
          </UserGuard>
        }
      />
      <Route
        path="/:eventId/editchallenges"
        element={
          <AdminGuard claims={claims}>
            <EditChallenges />
          </AdminGuard>
        }
      />

      <Route
        path="/:eventId/oracle"
        element={
          <OracleGuard claims={claims}>
            <OracleDashboard />
          </OracleGuard>
        }
      />
      <Route
        path="/:eventId/oracle/:challengeId/teams"
        element={
          <OracleGuard claims={claims}>
            <OracleTeamPicker />
          </OracleGuard>
        }
      />
      <Route
        path="/:eventId/oracle/:challengeId/teams/:teamId"
        element={
          <OracleGuard claims={claims}>
            <OracleTeam />
          </OracleGuard>
        }
      />

      <Route path="/:eventId/login" element={<LoginRedirect />} />
      <Route path="/" element={<LandingRedirect />} />
      <Route path="*" element={<Fallback />} />
    </Routes>
  );
};
